import React, {useState} from 'react';
import {Typography, Box} from '@mui/material';
import {truncateHtmlAtWords} from '@jobseeker/common-core/utils';

interface BenefitItemProps {
  name: string;
  description: string;
  isAppIimjobs: boolean;
  truncateLength?: number; // optional, default to 120
}

const isHtml = (str: string) => /<[a-z][\s\S]*>/i.test(str);

const getPlainText = (html: string) =>
  html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const BenefitItem: React.FC<BenefitItemProps> = ({
  name,
  description,
  isAppIimjobs,
  truncateLength = 120,
}) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => setExpanded(prev => !prev);

  const descIsHtml = isHtml(description);
  const plainText = descIsHtml ? getPlainText(description) : description;
  const isTruncated = plainText.length > truncateLength;

  const displayedText = (() => {
    if (!isTruncated || expanded) return description;
    if (descIsHtml) {
      // count words in the first truncateLength plain-text chars, use that as word limit
      const plainSlice = plainText.slice(0, truncateLength);
      const wordCount = plainSlice.trim().split(/\s+/).filter(w => w.length > 0).length;
      return truncateHtmlAtWords(description, wordCount);
    }
    const slice = description.slice(0, truncateLength);
    const lastSpace = slice.lastIndexOf(' ');
    return slice.slice(0, lastSpace > 0 ? lastSpace : truncateLength).trimEnd();
  })();

  return (
    <Box bgcolor="inherit">
      <Typography
        component="div"
        variant="subtitle3"
        lineHeight={'16px'}
        display="block"
        fontWeight={500}
        fontSize={isAppIimjobs ? 12 : 14}
      >
        {name}
      </Typography>
      {displayedText?.length > 0 && (
        <Typography
          component="span"
          sx={{
            display: 'inline-block',
            color: 'text.secondary',
            fontSize: isAppIimjobs ? 12 : 14,
            fontWeight: {xxs: 400, md: 500},
            lineHeight: '16px',
            opacity: isAppIimjobs ? 1 : 0.7,
            mt: 0.5,
            verticalAlign: 'middle',
          }}
        >
          <Box
            component="span"
            sx={{display: 'inline', lineHeight: 'inherit'}}
            {...(descIsHtml
              ? {dangerouslySetInnerHTML: {__html: displayedText}}
              : {children: displayedText})}
          />

          {isTruncated && (
            <Box
              component="span"
              onClick={toggleExpand}
              sx={{
                display: 'inline',
                cursor: 'pointer',
                ml: 0.5,
                fontSize: isAppIimjobs ? 12 : 14,
                fontWeight: 400,
                color: 'primary.main',
                lineHeight: 'inherit',
                verticalAlign: 'middle',
                userSelect: 'none',
              }}
            >
              {expanded ? 'Read less' : 'Read more'}
            </Box>
          )}
        </Typography>
      )}
    </Box>
  );
};

export default BenefitItem;
